import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';

const FAVORITES_KEY = 'noblex_favorites';
const RECENT_PAGES_KEY = 'noblex_recent_pages';
const ACTIVE_MODULE_KEY = 'noblex_active_module';

export interface RecentPage {
  path: string;
  title: string;
  icon?: string;
  timestamp: number;
}

interface NavigationContextType {
  activeModule: string;
  setActiveModule: (module: string) => void;
  recentPages: RecentPage[];
  addRecentPage: (page: RecentPage) => void;
  favorites: string[];
  toggleFavorite: (path: string) => void;
  isFavorite: (path: string) => boolean;
  clearRecentPages: () => void;
}

const NavigationContext = createContext<NavigationContextType | undefined>(undefined);

export const NavigationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const location = useLocation();
  const [activeModule, setActiveModuleState] = useState<string>(() => {
    return localStorage.getItem(ACTIVE_MODULE_KEY) || 'operations';
  });
  const [favorites, setFavorites] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem(FAVORITES_KEY);
      return saved ? JSON.parse(saved) : [];
    } catch (error) {
      console.error('Error loading favorites:', error);
      return [];
    }
  });
  const [recentPages, setRecentPages] = useState<RecentPage[]>(() => {
    try {
      const saved = localStorage.getItem(RECENT_PAGES_KEY);
      return saved ? JSON.parse(saved) : [];
    } catch (error) {
      console.error('Error loading recent pages:', error);
      return [];
    }
  });

  // Set active module with persistence
  const setActiveModule = useCallback((module: string) => {
    setActiveModuleState(module);
    localStorage.setItem(ACTIVE_MODULE_KEY, module);
  }, []);

  // Toggle favorite
  const toggleFavorite = useCallback((path: string) => {
    setFavorites((prev) => {
      const newFavorites = prev.includes(path)
        ? prev.filter((p) => p !== path)
        : [...prev, path];
      localStorage.setItem(FAVORITES_KEY, JSON.stringify(newFavorites));
      return newFavorites;
    });
  }, []);

  // Check if path is favorited
  const isFavorite = useCallback(
    (path: string) => {
      return favorites.includes(path);
    },
    [favorites]
  );

  // Add recent page
  const addRecentPage = useCallback((page: RecentPage) => {
    setRecentPages((prev) => {
      // Remove if already exists
      const filtered = prev.filter((p) => p.path !== page.path);
      // Add to front
      const updated = [page, ...filtered].slice(0, 10); // Keep only last 10
      localStorage.setItem(RECENT_PAGES_KEY, JSON.stringify(updated));
      return updated;
    });
  }, []);

  // Clear recent pages
  const clearRecentPages = useCallback(() => {
    setRecentPages([]);
    localStorage.removeItem(RECENT_PAGES_KEY);
  }, []);

  // Auto-detect active module from current path
  useEffect(() => {
    const path = location.pathname;
    console.log('[NavigationContext] Path changed to:', path);
    
    // Determine module from path - Oracle-style domains
    if (
      path === '/' ||
      path === '/dashboard' ||
      path.startsWith('/dashboard/') ||
      path.startsWith('/home') ||
      path === '/unified' ||
      path.startsWith('/unified/') ||
      path.startsWith('/reports')
    ) {
      console.log('[NavigationContext] Setting active module to: home');
      setActiveModule('home');
    } else if (path.startsWith('/growth') || path.startsWith('/sales') || path.startsWith('/crm')) {
      console.log('[NavigationContext] Setting active module to: sales');
      setActiveModule('sales');
    } else if (path.startsWith('/scm') || path.startsWith('/procurement') || path.startsWith('/inventory')) {
      console.log('[NavigationContext] Setting active module to: scm');
      setActiveModule('scm');
    } else if (path.startsWith('/mfg') || path.startsWith('/manufacturing')) {
      console.log('[NavigationContext] Setting active module to: mfg');
      setActiveModule('mfg');
    } else if (path.startsWith('/asset')) {
      console.log('[NavigationContext] Setting active module to: asset');
      setActiveModule('asset');
    } else if (path.startsWith('/fin') || path.startsWith('/finance')) {
      console.log('[NavigationContext] Setting active module to: fin');
      setActiveModule('fin');
    } else if (
      path.startsWith('/qms') ||
      path.startsWith('/grc') ||
      path.startsWith('/quality') ||
      path.startsWith('/risk') ||
      path.startsWith('/competency') ||
      path.startsWith('/calibration') ||
      path.startsWith('/compliance')
    ) {
      console.log('[NavigationContext] Setting active module to: qms');
      setActiveModule('qms');
    } else if (path.startsWith('/prj') || path.startsWith('/projects')) {
      console.log('[NavigationContext] Setting active module to: prj');
      setActiveModule('prj');
    } else if (path === '/education' || path.startsWith('/education/')) {
      console.log('[NavigationContext] Setting active module to: edu');
      setActiveModule('edu');
    } else if (path.startsWith('/hr')) {
      console.log('[NavigationContext] Setting active module to: hr');
      setActiveModule('hr');
    } else if (path.startsWith('/analytics')) {
      console.log('[NavigationContext] Setting active module to: analytics');
      setActiveModule('analytics');
    } else if (path.startsWith('/platform') || path.startsWith('/settings') || path.startsWith('/modules/masters')) {
      console.log('[NavigationContext] Setting active module to: platform');
      setActiveModule('platform');
    }
  }, [location.pathname, setActiveModule]);

  const value: NavigationContextType = {
    activeModule,
    setActiveModule,
    recentPages,
    addRecentPage,
    favorites,
    toggleFavorite,
    isFavorite,
    clearRecentPages,
  };

  return <NavigationContext.Provider value={value}>{children}</NavigationContext.Provider>;
};

export const useNavigation = () => {
  const context = useContext(NavigationContext);
  if (context === undefined) {
    throw new Error('useNavigation must be used within a NavigationProvider');
  }
  return context;
};

